import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { removeDish, updateDishName, cloneDish, updateDishSalesPrice } from "../api/api_dishes";
import { ReactComponent as ClimateIcon } from "../assets/icons/svg/Klimat-gron.svg";
// import { ReactComponent as WaterIcon } from "../assets/icons/svg/Vatten.svg";
import { ReactComponent as HealthIcon } from "../assets/icons/svg/Halsa-plus.svg";
import { ReactComponent as SocialIcon } from "../assets/icons/svg/Social-rattvisa.svg";
// getColor not used; rely on TotalScore visuals
import { useTranslation } from "../hooks/useTranslation";
import TotalScore from "./TotalScore";
import { useAppAlertDialog } from "../hooks/useAppAlertDialog";
import PriceMatricsListCompact from "./PriceMatricsListCompact";

const TableRowItem = ({ item, onDelete, onUpdate, onCopy }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(item.name);
  const [currentSalesPrice, setCurrentSalesPrice] = useState(item?.sales_price_total ?? null);
  const [editSalesPrice, setEditSalesPrice] = useState(
    item?.sales_price_total === null || item?.sales_price_total === undefined
      ? ""
      : String(item.sales_price_total)
  );
  const [isCopying, setIsCopying] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyName, setCopyName] = useState(`${item.name} (Copy)`);
  const nameInputRef = useRef(null);
  const copyNameInputRef = useRef(null);
  const prevItemIdRef = useRef(item.id);
  const { showAlert, showConfirm, dialogNode } = useAppAlertDialog({
    cancelLabel: t("Avbryt"),
    confirmLabel: t("Fortsätt"),
    closeLabel: t("Stäng"),
  });

  // Keep list row in sync when parent refetches or filters refresh `item` (avoid stale sales price / name)
  useEffect(() => {
    const idChanged = prevItemIdRef.current !== item.id;
    prevItemIdRef.current = item.id;

    const applyServerItem = () => {
      setEditName(item.name);
      const sp = item?.sales_price_total ?? null;
      setCurrentSalesPrice(sp);
      setEditSalesPrice(sp === null || sp === undefined ? "" : String(sp));
    };

    if (idChanged) {
      setIsEditing(false);
      applyServerItem();
      return;
    }
    if (isEditing) return;
    applyServerItem();
  }, [item.id, item.name, item.sales_price_total, isEditing]);

  const fullName = (item?.name || "").toString();
  const maxNameChars = 40;
  const truncatedName =
    fullName.length > maxNameChars
      ? `${fullName.slice(0, maxNameChars)}...`
      : fullName;
  const sellPrice = currentSalesPrice ?? null;
  const costPrice = item?.purchase_price_total ?? null;
  const marginValue = item?.price_difference ?? null;

  // const getAverageScore = (dish) => {
    
  //  let delimeter = 0;
  //  let totalScores = 0;
  //  if(dish.environment_average) {
  //   delimeter +=1 ;
  //   totalScores += dish.environment_average;
  //  } 
  //  if(dish.health_average) {
  //   delimeter +=1 ;
  //   totalScores += dish.health_average;
  //  } 
  //  if(dish.social_average) {
  //   delimeter +=1 ;
  //   totalScores += dish.social_average;
  //  } 
    
  //   return Math.round(totalScores / delimeter);
  // };

 

  const handleDelete = async () => {
    const confirmed = await showConfirm(
      t("Ta bort recept"),
      t(`Är du säker på att du vill ta bort "${item.name}"?`)
    );
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    try {
      await removeDish(item.id);
      console.log(t("Rätt borttagen"));
      if (onDelete) {
        onDelete(item.id);
      }
    } catch (error) {
      console.error(t("Fel vid borttagning av rätt:"), error);
      showAlert(t("Fel"), t("Misslyckades att ta bort rätten. Försök igen."));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditName(item.name);
    setEditSalesPrice(
      currentSalesPrice === null || currentSalesPrice === undefined
        ? ""
        : String(currentSalesPrice)
    );
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }
    }, 100);
  };

  const handleSave = async () => {
    if (editName.trim() === '') {
      showAlert(t("Fel"), t('Namnet kan inte vara tomt'));
      return;
    }

    const salesInput = editSalesPrice.trim();
    if (salesInput !== "" && (!Number.isFinite(Number(salesInput)) || Number(salesInput) < 0)) {
      showAlert(t("Fel"), t("Försäljningspris måste vara ett giltigt tal"));
      return;
    }

    const nextSalesPrice = salesInput === "" ? null : Number(salesInput);
    const currentSalesNumber =
      currentSalesPrice === null || currentSalesPrice === undefined || currentSalesPrice === ""
        ? null
        : Number(currentSalesPrice);

    const nameChanged = editName !== item.name;
    const salesChanged = currentSalesNumber !== nextSalesPrice;

    if (!nameChanged && !salesChanged) {
      setIsEditing(false);
      return;
    }

    try {
      if (nameChanged) {
        await updateDishName(item.id, editName);
      }
      if (salesChanged) {
        await updateDishSalesPrice(item.id, nextSalesPrice);
        setCurrentSalesPrice(nextSalesPrice);
      }
      if (onUpdate && nameChanged) {
        onUpdate(item.id, editName);
      }
      setIsEditing(false);
    } catch (error) {
      console.error(t("Fel vid uppdatering av recept:"), error);
      showAlert(t("Fel"), t("Misslyckades att uppdatera receptet. Försök igen."));
      setEditName(item.name);
      setEditSalesPrice(
        currentSalesPrice === null || currentSalesPrice === undefined
          ? ""
          : String(currentSalesPrice)
      );
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditName(item.name);
    setEditSalesPrice(
      currentSalesPrice === null || currentSalesPrice === undefined
        ? ""
        : String(currentSalesPrice)
    );
  };

  const handleCopyClick = () => {
    setShowCopyDialog(true);
    setCopyName(`${item.name} (Copy)`);
    setTimeout(() => {
      if (copyNameInputRef.current) {
        copyNameInputRef.current.focus();
        copyNameInputRef.current.select();
      }
    }, 100);
  };

  const handleCopyConfirm = async () => {
    if (copyName.trim() === '') {
      showAlert(t("Fel"), t('Namnet kan inte vara tomt'));
      return;
    }

    setIsCopying(true);
    try {
      const response = await cloneDish(item.id, copyName.trim());
      console.log(t("Recept kopierat:"), response.data);
      setShowCopyDialog(false);
      if (onCopy) {
        onCopy(response.data.dish_id, copyName.trim());
      }
    } catch (error) {
      console.error(t("Fel vid kopiering av recept:"), error);
      showAlert(t("Fel"), t("Misslyckades att kopiera receptet. Försök igen."));
    } finally {
      setIsCopying(false);
    }
  };

  const handleCopyCancel = () => {
    setShowCopyDialog(false);
    setCopyName(`${item.name} (Copy)`);
  };

  // const avgScore = getAverageScore(item);
  // const sustainability_values = {
  //   climate: item.climate_score || 0,
  //   water: item.water_score || 0,
  //   health: item.health_score || 0,
  //   social: item.social_justice_score || 0
  // };

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-4 w-[34%] max-w-0">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12">
            {item.image ? (
              <img 
                className="h-12 w-12 rounded-xl object-cover shadow-sm" 
                src={item.image.replace(/\\/g, '/')} 
                alt={item.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm" style={{ display: item.image ? 'none' : 'flex' }}>
              <img 
                src="/assets/icons/Innehall.png" 
                alt="No bild" 
                className="h-6 w-6 text-gray-400"
              />
            </div>
          </div>
          <div className="ml-4">
        {isEditing ? (
          <div className="flex items-center space-x-2">
            <div className="space-y-1">
              <input
                ref={nameInputRef}
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSave();
                  } else if (e.key === 'Escape') {
                    handleCancel();
                  }
                }}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent"
                placeholder="Rättnamn"
              />
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">{t("Försäljning")}:</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editSalesPrice}
                  onChange={(e) => setEditSalesPrice(e.target.value)}
                  className="w-28 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="flex space-x-2">
            <button
              onClick={handleSave}
                    className="bg-secondary-green hover:bg-green-700 text-white px-3 py-1 rounded-lg transition-colors duration-200"
                    title="Spara"
            >
                    <img 
                      src="/assets/icons/Check.png" 
                      alt="Save" 
                      className="w-4 h-4"
                    />
            </button>
            <button
              onClick={handleCancel}
                    className="bg-gray-200 hover:bg-gray-300 text-white px-3 py-1 rounded-lg transition-colors duration-200"
                    title={t("Avbryt")}
            >
                    <img 
                      src="/assets/icons/svg/Stang.svg" 
                      alt="Cancel" 
                      className="w-6 h-4"
                    />
            </button>
                </div>
          </div>
        ) : (
              <div className="min-w-0">
                <div className="flex items-center min-w-0">
                  <span
                    className=" capitalize text-lg font-semibold text-gray-900 block truncate max-w-full"
                    title={fullName}
                  >
                    {truncatedName}
                  </span>
                </div>
                <div className="mt-1">
                  <div className="max-w-[22rem]">
                    <PriceMatricsListCompact
                      sellPrice={sellPrice}
                      cost={costPrice}
                      margin={marginValue}
                    />
                  </div>
                </div>
              </div>
        )}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap">
        <div className="flex items-center justify-center">
          {/* <span 
            className="sustainability-score text-sm font-semibold"
            style={{ 
              backgroundColor: getColor(avgScore) + '80',
              color: avgScore >= 60 ? '#1f2937' : '#ffffff',
              border: `1px solid ${getColor(avgScore)}`
            }}
          >
            {avgScore}%
          </span> */}
          <TotalScore dish={item} size="small" />
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-4 justify-center">
          <div className="flex space-x-2">
            <div className="flex items-center space-x-1">
              <ClimateIcon className="w-8 h-8 text-secondary-climateGreen" />
              <span className="text-sm text-gray-600">{item.environment_average}</span>
            </div>
            {/* <div className="flex items-center space-x-1">
              <WaterIcon className="w-8 h-8 text-secondary-waterBlue" />
              <span className="text-sm text-gray-600">{sustainability_values.water}</span>
            </div> */}
            <div className="flex items-center space-x-1">
              <HealthIcon className="w-8 h-8 text-secondary-healthPink" />
              <span className="text-sm text-gray-600">{item.health_average}</span>
            </div>
            <div className="flex items-center space-x-1">
              <SocialIcon className="w-8 h-8 text-secondary-socialOrange" />
              <span className="text-sm text-gray-600">{item.social_average}</span>
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
        <Link
            to={`/items/${item.id}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-secondary-green hover:bg-secondary-green/80 text-white transition-colors"
            title={t("Visa detaljer")}
          >
            <img 
              src="/assets/icons/svg/Visa.svg" 
              alt="View Details" 
              className="w-4 h-4"
            />
          </Link>
          {!isEditing && (
            <button 
              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-yellow-500 hover:bg-yellow-600 text-white transition-colors"
              onClick={handleEdit}
              title={t("Redigera Namn")}
            >
              <img 
                src="/assets/icons/Redigera.png" 
                alt="Edit" 
                className="w-4 h-4"
              />
            </button>
          )}
          {!isEditing && (
            <button 
              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-white hover:bg-gray-200 text-white transition-colors disabled:opacity-50 border border-gray-300"
              onClick={handleCopyClick}
              disabled={isCopying}
              title={t("Kopiera Recept")}
            >
              {isCopying ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <img 
                  src="/assets/icons/svg/Copiera.svg" 
                  alt="Copy" 
                  className="w-4 h-4"
                />
              )}
            </button>
          )}
          
          <button 
            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-200 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
            onClick={handleDelete}
            disabled={isDeleting}
            title={t("Ta bort")}
          >
            {isDeleting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <img 
                src="/assets/icons/Radera.png" 
                alt="Delete" 
                className="w-4 h-4"
              />
            )}
          </button>
        </div>
      </td>
      </tr>
      {/* Copy Confirmation Dialog */}
      {showCopyDialog && (
        <tr>
          <td colSpan="3" className="px-6 py-4 bg-blue-50 border-t border-blue-200">
            <div className="bg-white rounded-lg p-4 shadow-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{t("Kopiera Recept")}</h3>
                <button
                  onClick={handleCopyCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <img 
                    src="/assets/icons/Dolj.png" 
                    alt="Close" 
                    className="w-5 h-5"
                  />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {t("Ange ett namn för den kopierade versionen av")} "{item.name}":
              </p>
              <div className="flex items-center space-x-3">
                <input
                  ref={copyNameInputRef}
                  type="text"
                  value={copyName}
                  onChange={(e) => setCopyName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCopyConfirm();
                    } else if (e.key === 'Escape') {
                      handleCopyCancel();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t("Namn på kopierat recept")}
                />
                <button
                  onClick={handleCopyConfirm}
                  disabled={isCopying || copyName.trim() === ''}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  {isCopying ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t("Kopierar...")}</span>
                    </>
                  ) : (
                    <>
                      <img 
                        src="/assets/icons/svg/Copiera.svg" 
                        alt="Copy" 
                        className="w-4 h-4"
                      />
                      <span>{t("Kopiera")}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleCopyCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                >
                  {t("Avbryt")}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
      {dialogNode}
    </>
  );
};

export default TableRowItem;
